import React, { useState, useEffect } from "react";
import { Calendar as CalendarIcon, Clock, CheckCircle2, Loader2, MapPin } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { scheduleOrder, fetchBlockedDates, fetchSlotAvailability } from "../lib/api";
import { useToast } from "../hooks/use-toast";

// Pick-up hours: 9 AM–5 PM on Wednesday, Saturday & Sunday
const PICKUP_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM",
  "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM",
];

// Photography: any day, hourly 9 AM–9 PM
const PHOTO_SLOTS = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM",
  "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM",
];

// Pick-up only on Wednesday (3), Saturday (6) and Sunday (0)
const PICKUP_DAYS = [0, 3, 6];

const SchedulePicker = ({ sessionId, kind, onScheduled }) => {
  const isBooking = kind === "booking";
  const { toast } = useToast();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [blocked, setBlocked] = useState([]);
  const [slots, setSlots] = useState({ capacity: 5, counts: {} });

  useEffect(() => {
    fetchBlockedDates()
      .then((rows) => setBlocked((rows || []).map((r) => r.date)))
      .catch(() => {});
  }, []);

  // Fetch how many bookings already fill each slot for the chosen date
  useEffect(() => {
    if (!date) { setSlots({ capacity: 5, counts: {} }); return; }
    setTime(null);
    fetchSlotAvailability(format(date, "EEE, MMM d, yyyy"), isBooking ? "booking" : "pickup")
      .then((r) => setSlots({ capacity: r.capacity || 5, counts: r.counts || {} }))
      .catch(() => setSlots({ capacity: 5, counts: {} }));
  }, [date, isBooking]);

  const timeSlots = isBooking ? PHOTO_SLOTS : PICKUP_SLOTS;
  const startOfToday = new Date(new Date().setHours(0, 0, 0, 0));
  const isDisabledDay = (d) => {
    if (d < startOfToday) return true;
    if (!isBooking && !PICKUP_DAYS.includes(d.getDay())) return true; // pickup: Wed/Sat/Sun only
    if (blocked.includes(format(d, "yyyy-MM-dd"))) return true; // holidays / blocked
    return false;
  };

  const confirm = async () => {
    if (!date || !time) {
      toast({ title: "Please choose a date and time", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await scheduleOrder(sessionId, { date: format(date, "EEE, MMM d, yyyy"), time });
      onScheduled({ date: format(date, "EEE, MMM d, yyyy"), time });
    } catch (err) {
      toast({ title: "Couldn't save", description: err?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 bg-white rounded-2xl border border-[#0e2a1e]/10 p-6 text-left">
      <h2 className="font-serif-brand text-2xl font-semibold text-[#0e2a1e]">
        {isBooking ? "Choose your session date & time" : "Choose your pick-up date & time"}
      </h2>
      <p className="text-sm text-[#0e2a1e]/60 mt-1">
        {isBooking
          ? "Pick a preferred slot — we'll confirm the exact location by email."
          : "Select when you'd like to collect your order."}
      </p>

      {!isBooking && (
        <div className="flex items-center gap-2 text-sm text-[#0e2a1e]/70 mt-3">
          <MapPin size={16} className="text-[#2f6b1e]" />
          <span>85 Acorn Place, Mississauga, ON</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        {/* Date */}
        <div>
          <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Date</label>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button className="w-full mt-1.5 flex items-center gap-2 bg-[#f4f2ea] border border-[#0e2a1e]/15 rounded-md px-4 py-2.5 text-sm text-left hover:border-[#0e2a1e]/40 transition-colors">
                <CalendarIcon size={16} className="text-[#0e2a1e]/60" />
                {date ? format(date, "EEE, MMM d, yyyy") : <span className="text-[#0e2a1e]/50">Select a date</span>}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => { setDate(d); setOpen(false); }}
                disabled={isDisabledDay}
                initialFocus
              />
            </PopoverContent>
          </Popover>
          {!isBooking && (
            <p className="text-[11px] text-[#0e2a1e]/50 mt-1.5">Pick-up available on Wednesdays, Saturdays &amp; Sundays only, 9 AM&ndash;5 PM.</p>
          )}
        </div>

        {/* Time */}
        <div>
          <label className="text-xs font-semibold text-[#0e2a1e]/70 uppercase tracking-wide">Time</label>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {timeSlots.map((t) => {
              const booked = slots.counts[t] || 0;
              const full = booked >= slots.capacity;
              const disabled = !date || full;
              return (
                <button
                  key={t}
                  disabled={disabled}
                  onClick={() => !disabled && setTime(t)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors flex items-center gap-1.5 ${
                    full
                      ? "bg-[#0e2a1e]/5 text-[#0e2a1e]/35 border-[#0e2a1e]/10 cursor-not-allowed line-through"
                      : time === t
                      ? "bg-[#0e2a1e] text-[#f4f2ea] border-[#0e2a1e]"
                      : "bg-[#f4f2ea] text-[#0e2a1e] border-[#0e2a1e]/15 hover:border-[#0e2a1e]/40"
                  } ${!date ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid={`slot-${t.replace(/[:\s]/g, "")}`}
                >
                  {t}
                  {date && (full
                    ? <span className="text-[10px] font-semibold">Full</span>
                    : booked > 0 && <span className={`text-[10px] ${time === t ? "text-[#f4f2ea]/70" : "text-[#2f6b1e]"}`}>{booked} booked</span>)}
                </button>
              );
            })}
          </div>
          {!date && <p className="text-[11px] text-[#0e2a1e]/50 mt-1.5">Pick a date first to see available times.</p>}
          {isBooking && (
            <p className="text-[11px] text-[#0e2a1e]/50 mt-1.5">Photography sessions available any day, 9 AM–9 PM.</p>
          )}
        </div>
      </div>

      <button
        onClick={confirm}
        disabled={saving}
        className="w-full mt-6 bg-[#a4d61e] text-[#0e2a1e] font-semibold py-3.5 rounded-full hover:bg-[#b6e63a] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
      >
        {saving ? (<><Loader2 size={18} className="animate-spin" /> Saving…</>) : (<><CheckCircle2 size={18} /> Confirm {isBooking ? "session" : "pick-up"} time</>)}
      </button>
    </div>
  );
};

export default SchedulePicker;
